import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { runSlaNudges, money } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { TimerOff, Timer, BellRing, Loader2, ArrowUpRight, FileSearch } from "lucide-react";
import { Button } from "@/components/ui/button";

export const ReviewSla = ({ due, onChanged }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!due) return null;
  const breached = due.review_breached || [];
  const risk = due.review_at_risk || [];

  const nudge = async () => {
    setBusy(true);
    try {
      const r = await runSlaNudges();
      const parts = [];
      if (r.escalated_count) parts.push(`escalated ${r.escalated_count}`);
      if (r.count) parts.push(`nudged ${r.count}`);
      toast.success(parts.length ? `${parts.join(", ")} invoice(s)` : "Nothing to nudge — everyone was reminded in the last 24h");
      onChanged?.();
    } catch {
      toast.error("Could not send nudges");
    }
    setBusy(false);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="review-sla">
      <div className={cn("rounded-xl border bg-card overflow-hidden lg:col-span-2", breached.length ? "border-red-500/40" : "border-border")} data-testid="sla-column">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className={cn("flex items-center gap-2 text-sm font-medium", breached.length ? "text-red-500" : "text-muted-foreground")}>
            {breached.length ? <TimerOff className="h-4 w-4" /> : <Timer className="h-4 w-4" />} Review SLA
            <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-mono text-foreground" data-testid="sla-breached-count">{breached.length}</span>
            <span className="text-xs text-muted-foreground font-normal">breached · {risk.length} due in &lt;12h · {due.review_pending} pending</span>
          </div>
          <Button data-testid="sla-nudge-btn" size="sm" variant="ghost" onClick={nudge} disabled={busy || !breached.length}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5 mr-1.5" />} Nudge now
          </Button>
        </div>
        {breached.length === 0 && risk.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-muted-foreground" data-testid="sla-empty">Every flagged invoice is inside its review deadline.</div>
        ) : (
          <ul className="divide-y divide-border max-h-56 overflow-auto d20-scroll">
            {[...breached, ...risk].map((r) => (
              <li key={r.id}>
                <button data-testid={`sla-row-${r.id}`} onClick={() => navigate(`/invoice/${r.id}`)} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/40">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.supplier || "Unknown"} <span className="font-mono text-xs text-muted-foreground">{r.invoice_number}</span></div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                      <span>{r.assigned_to ? `assigned to ${r.assigned_to}` : "unassigned"}{r.nudge_count ? ` · nudged ×${r.nudge_count}` : ""}</span>
                      {r.escalated && <span data-testid={`sla-escalated-${r.id}`} className="rounded border border-red-500/40 px-1.5 py-0.5 text-[10px] font-semibold text-red-500">Escalated from {r.escalated.from || "unassigned"}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-sm">{money(r.total, r.currency)}</div>
                    <div className={cn("text-[11px] font-semibold", r.hours_left < 0 ? "text-red-500" : "text-amber-500")}>
                      {r.hours_left < 0 ? `${Math.abs(Math.round(r.hours_left))}h over` : `${Math.round(r.hours_left)}h left`}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button data-testid="chases-kpi" onClick={() => navigate("/suppliers#chases")} className={cn("rounded-xl border bg-card p-5 text-left hover:border-primary/50 transition-colors", due.open_chases ? "border-amber-500/40" : "border-border")}>
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><FileSearch className="h-4 w-4 text-amber-500" /> Missing invoices to chase <ArrowUpRight className="h-3.5 w-3.5 ml-auto" /></div>
        <div className="mt-3 font-mono text-3xl font-bold" data-testid="chases-kpi-count">{due.open_chases}</div>
        <div className="text-xs text-muted-foreground mt-1">On supplier statements but never received. Auto-clears when the copy arrives.</div>
      </button>
    </div>
  );
};
